import React from "react";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CollectionsBookmarkRoundedIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import ContrastRoundedIcon from '@mui/icons-material/ContrastRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EditOffRoundedIcon from '@mui/icons-material/EditOffRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import TextField from "@mui/material/TextField";

import {useTranslation} from "react-i18next";
import {useNavigate, useLocation} from "react-router-dom";

import UserToken from "./UserToken";
import LngToken from "./LngToken";
import {APP_NAME} from "../../App";
import {useAuth} from "../../context/AuthContext";
import {useAppContext} from "../../context/AppContext";
import {useThemeContext} from "../../context/ThemeContext";
import {useToast} from "../../context/ToastContext";
import ApiCmsCtrl from "../../api/ApiCmsCtrl";

/** Correspondance type de page → chemin de navigation */
const PAGE_ROUTES = {
    welcome:    "/",
    reports:    "/reports",
    imports:    "/private/import",
    explore:    "/explore",
    dashboards: "/dashboards",
    views:      "/private/folders",
    analytics:  "/private/analyses",
    origins:    "/origin",
    manuel:     "/howto",
    contact:    "/contact",
};

/** Icône sémantique par type de page */
const PAGE_ICONS = {
    welcome:    <HomeRoundedIcon fontSize="small"/>,
    imports:    <CloudUploadRoundedIcon fontSize="small"/>,
    explore:    <PublicRoundedIcon fontSize="small"/>,
    dashboards: <DashboardRoundedIcon fontSize="small"/>,
    views:      <FolderOpenRoundedIcon fontSize="small"/>,
    analytics:  <BarChartRoundedIcon fontSize="small"/>,
    origins:    <AccountTreeRoundedIcon fontSize="small"/>,
    manuel:     <MenuBookRoundedIcon fontSize="small"/>,
    contact:    <EmailRoundedIcon fontSize="small"/>,
    reports:    <AssessmentRoundedIcon fontSize="small"/>,
};

/** Types de pages regroupés dans le menu "Mes outils" */
const TOOLS_TYPES = new Set(["explore", "views", "analytics", "imports"]);

/** Ordre d'affichage fixe dans le menu déroulant */
const TOOLS_ORDER = ["explore", "views", "analytics", "imports"];

/** Style pill partagé pour tous les boutons nav */
const pillSx = (isActive, adminStyle = false) => ({
    textTransform: "none",
    fontWeight: isActive ? 700 : 400,
    borderRadius: "20px",
    px: 1.5,
    py: 0.55,
    minWidth: "auto",
    bgcolor: isActive ? "rgba(255,255,255,0.22)" : "transparent",
    border: adminStyle ? "1px solid rgba(255,255,255,0.35)" : "none",
    transition: "background-color 0.2s ease",
    "& .MuiButton-startIcon": { opacity: isActive ? 1 : 0.8, mr: 0.5 },
    "&:hover": { bgcolor: isActive ? "rgba(255,255,255,0.30)" : "rgba(255,255,255,0.13)" },
});

/** Bouton de navigation avec indicateur pill actif */
function NavButton({label, path, startIcon, navigate, isActive, adminStyle = false}) {
    return (
        <Button
            startIcon={startIcon}
            onClick={() => navigate(path)}
            color="inherit"
            size="small"
            sx={pillSx(isActive, adminStyle)}
        >
            {label}
        </Button>
    );
}

/** Séparateur vertical léger */
const VDivider = () => (
    <Divider orientation="vertical" flexItem sx={{bgcolor: "rgba(255,255,255,0.18)", mx: 0.5}}/>
);

export default function BaBarre({disconnect}) {
    const {user, pendingCount} = useAuth();
    const {config, setConfig, editMode, setEditMode} = useAppContext();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {paletteMode, setPaletteMode, PALETTES, darkMode, toggleDarkMode} = useThemeContext();
    const {showToast} = useToast();

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const [anchorElTools, setAnchorElTools] = React.useState(null);
    const openTools = Boolean(anchorElTools);

    const [anchorElDisplay, setAnchorElDisplay] = React.useState(null);
    const openDisplay = Boolean(anchorElDisplay);

    const [anchorElPages, setAnchorElPages] = React.useState(null);
    const openPages = Boolean(anchorElPages);

    const [saving, setSaving] = React.useState(false);
    const [newReportName, setNewReportName] = React.useState("");

    const userRole = user?.instances?.[APP_NAME]?.role;
    const isAdmin = userRole === "ADMIN";
    const canEdit = userRole === "ADMIN" || userRole === "WRITER";

    // Désactiver le mode édition si l'utilisateur perd son rôle (déconnexion, timeout)
    React.useEffect(() => {
        if (!canEdit && editMode) {
            setEditMode(false);
        }
    }, [canEdit, editMode, setEditMode]);

    /** Toggle la visibilité d'un type de page dans config.pages */
    function togglePageVisibility(type) {
        setConfig(prev => {
            const pages = prev.pages ? [...prev.pages] : [];
            const idx = pages.findIndex(p => p.type === type);
            if (idx >= 0) {
                // Retirer la page
                pages.splice(idx, 1);
            } else {
                // Ajouter la page avec config par défaut
                pages.push({type, auth: false, roles: [], config: {}});
            }
            return {...prev, pages};
        });
    }

    /** Drag & drop state pour réordonner les pages */
    const dragItem = React.useRef(null);
    const dragOverItem = React.useRef(null);

    function handleDragStart(idx) {
        dragItem.current = idx;
    }

    function handleDragEnter(idx) {
        dragOverItem.current = idx;
    }

    function handleDragEnd() {
        if (dragItem.current === null || dragOverItem.current === null) return;
        if (dragItem.current === dragOverItem.current) return;
        setConfig(prev => {
            const pages = [...(prev.pages || [])];
            const [dragged] = pages.splice(dragItem.current, 1);
            pages.splice(dragOverItem.current, 0, dragged);
            return {...prev, pages};
        });
        dragItem.current = null;
        dragOverItem.current = null;
    }

    /** Sauvegarde la config CMS éditée */
    async function handleSave() {
        setSaving(true);
        const {error} = await ApiCmsCtrl.updateCmsTemplate(config);
        setSaving(false);
        if (error) {
            showToast({msg: t("cms.saveError", "Erreur lors de la sauvegarde"), severity: "error"});
        } else {
            showToast({msg: t("cms.saveSuccess", "Configuration sauvegardée"), severity: "success"});
        }
    }

    /** Ajoute un nouveau rapport dans config.reports */
    function addReport(name) {
        const key = name.trim().toUpperCase();
        if (!key) return;
        setConfig(prev => ({
            ...prev,
            reports: {...(prev.reports || {}), [key]: {}},
        }));
        setNewReportName("");
    }

    /** Supprime un rapport de config.reports */
    function deleteReport(key) {
        setConfig(prev => {
            const reports = {...(prev.reports || {})};
            delete reports[key];
            return {...prev, reports};
        });
    }

    const handleOpenReports = (event) => setAnchorEl(event.currentTarget);
    const handleCloseReports = () => setAnchorEl(null);

    const handleOpenTools = (event) => setAnchorElTools(event.currentTarget);
    const handleCloseTools = () => setAnchorElTools(null);


    /** Vérifie si l'utilisateur peut voir cette page */
    function canDisplay(page) {
        if (!page.auth) return true;
        if (!user) return false;
        if (!page.roles) return true;
        return page.roles.includes(user.instances[APP_NAME].role);
    }

    /** Vérifie si un chemin correspond à la route active */
    function isActive(path) {
        if (path === "/") return location.pathname === "/";
        return location.pathname.startsWith(path);
    }

    /** Rendu du menu déroulant fusionné "Mes outils" + "Admin" */
    function renderMergedMenu(toolsPages) {
        // Ordre fixe : Explorer → Dashboards → Vues → Analyses → Import
        const sortedPages = [...toolsPages].sort(
            (a, b) => TOOLS_ORDER.indexOf(a.type) - TOOLS_ORDER.indexOf(b.type)
        );
        const isToolsActive = sortedPages.some(p => {
            const path = PAGE_ROUTES[p.type];
            return path ? isActive(path) : false;
        });
        const isAdminActive = location.pathname.startsWith("/admin");
        const isMergedActive = isToolsActive || isAdminActive;
        const hasAnalytics = config.pages?.some(p => p.type === "analytics" && canDisplay(p));

        return (
            <React.Fragment key="merged-dropdown">
                <Badge
                    badgeContent={isAdmin ? pendingCount : 0}
                    color="error"
                    max={99}
                    invisible={!isAdmin || !pendingCount}
                    sx={{
                        "& .MuiBadge-badge": {
                            fontSize: 9, height: 16, minWidth: 16,
                            animation: pendingCount ? "pulse 1.5s ease-in-out infinite" : "none",
                            "@keyframes pulse": {
                                "0%":   {boxShadow: "0 0 0 0 rgba(211,47,47,0.7)"},
                                "70%":  {boxShadow: "0 0 0 5px rgba(211,47,47,0)"},
                                "100%": {boxShadow: "0 0 0 0 rgba(211,47,47,0)"},
                            },
                        },
                    }}
                >
                    <Button
                        startIcon={<HandymanRoundedIcon fontSize="small"/>}
                        endIcon={
                            <KeyboardArrowDownIcon sx={{
                                fontSize: "18px !important",
                                transition: "transform 0.2s",
                                transform: openTools ? "rotate(180deg)" : "rotate(0deg)",
                            }}/>
                        }
                        aria-controls={openTools ? "tools-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={openTools ? "true" : undefined}
                        onClick={handleOpenTools}
                        color="inherit"
                        size="small"
                        sx={pillSx(isMergedActive)}
                    >
                        {t("menu.tools")}
                    </Button>
                </Badge>
                <Menu
                    id="tools-menu"
                    anchorEl={anchorElTools}
                    open={openTools}
                    onClose={handleCloseTools}
                    elevation={4}
                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                    slotProps={{
                        paper: {sx: {minWidth: 220, mt: 0.5, borderRadius: 2, px: 0.5}},
                        list: {"aria-labelledby": "tools-button"},
                    }}
                >
                    {/* ── Outils personnels ── */}
                    {sortedPages.map((page) => {
                        const path = PAGE_ROUTES[page.type];
                        const icon = PAGE_ICONS[page.type] ?? null;
                        const labelKey = page.type === "analytics"  ? "menu.analyses"
                                       : page.type === "dashboards" ? "menu.dashboards"
                                       : `menu.${page.type}`;
                        return (
                            <MenuItem
                                key={page.type}
                                onClick={() => { navigate(path); handleCloseTools(); }}
                                sx={{borderRadius: 1.5, fontWeight: isActive(path) ? 700 : 400, gap: 1}}
                            >
                                {React.cloneElement(icon, {sx: {color: isActive(path) ? "primary.main" : "text.secondary"}})}
                                {t(labelKey)}
                            </MenuItem>
                        );
                    })}

                    {/* ── Section Administration (ADMIN uniquement) ── */}
                    {isAdmin && (
                        <Box>
                            <Divider sx={{my: 0.75}}/>
                            <Box sx={{px: 1.5, pb: 0.5}}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}
                                            sx={{letterSpacing: 0.5, textTransform: "uppercase"}}>
                                    Administration
                                </Typography>
                            </Box>

                            {/* Gestion des utilisateurs */}
                            <MenuItem
                                onClick={() => { navigate("/admin/users"); handleCloseTools(); }}
                                sx={{borderRadius: 1.5, fontWeight: location.pathname.startsWith("/admin/users") ? 700 : 400, gap: 1}}
                            >
                                <Badge
                                    badgeContent={pendingCount}
                                    color="error"
                                    max={99}
                                    invisible={!pendingCount}
                                    sx={{"& .MuiBadge-badge": {fontSize: 9, height: 16, minWidth: 16}}}
                                >
                                    <AdminPanelSettingsRoundedIcon fontSize="small" sx={{color: "text.secondary"}}/>
                                </Badge>
                                {t("menu.adminUsers")}
                            </MenuItem>

                            {hasAnalytics && <Divider sx={{my: 0.5}}/>}
                            {hasAnalytics && (
                                <MenuItem
                                    onClick={() => { navigate("/admin/analytics/collection"); handleCloseTools(); }}
                                    sx={{borderRadius: 1.5, fontWeight: location.pathname.startsWith("/admin/analytics/collection") ? 700 : 400, gap: 1}}
                                >
                                    <CollectionsBookmarkRoundedIcon fontSize="small" sx={{color: location.pathname.startsWith("/admin/analytics/collection") ? "primary.main" : "text.secondary"}}/>
                                    {t("menu.adminCollection")}
                                </MenuItem>
                            )}
                            {hasAnalytics && (
                                <MenuItem
                                    onClick={() => { navigate("/admin/analytics/manager"); handleCloseTools(); }}
                                    sx={{borderRadius: 1.5, fontWeight: location.pathname.startsWith("/admin/analytics/manager") ? 700 : 400, gap: 1}}
                                >
                                    <BarChartRoundedIcon fontSize="small" sx={{color: location.pathname.startsWith("/admin/analytics/manager") ? "primary.main" : "text.secondary"}}/>
                                    {t("menu.adminAnalytics")}
                                </MenuItem>
                            )}
                        </Box>
                    )}

                </Menu>
            </React.Fragment>
        );
    }

    function getButton(page, index) {
        if (!canDisplay(page)) return null;

        const icon = PAGE_ICONS[page.type] ?? null;

        if (page.type === "reports") {
            const isReportActive = location.pathname.startsWith("/reports");
            return (
                <React.Fragment key={index}>
                    <Button
                        startIcon={icon}
                        endIcon={
                            <KeyboardArrowDownIcon sx={{
                                fontSize: "18px !important",
                                transition: "transform 0.2s",
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            }}/>
                        }
                        aria-controls={open ? "reports-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleOpenReports}
                        color="inherit"
                        size="small"
                        sx={pillSx(isReportActive)}
                    >
                        {t("menu.reports")}
                    </Button>
                    <Menu
                        id="reports-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleCloseReports}
                        elevation={4}
                        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                        transformOrigin={{vertical: "top", horizontal: "left"}}
                        slotProps={{
                            paper: {sx: {minWidth: 200, mt: 0.5, borderRadius: 2, px: 0.5}},
                            list: {"aria-labelledby": "reports-button"},
                        }}
                    >
                        {config.reports && Object.keys(config.reports).map((report, i) => [
                            i > 0 && <Divider key={`divider-${i}`} sx={{my: 0.5}}/>,
                            <MenuItem
                                key={report}
                                selected={location.pathname === `/reports/${report.toLowerCase()}`}
                                onClick={() => { navigate("/reports/" + report.toLowerCase()); handleCloseReports(); }}
                                sx={{borderRadius: 1.5, fontWeight: 500, gap: 1}}
                            >
                                <SummarizeRoundedIcon fontSize="small" sx={{color: "text.secondary"}}/>
                                {report.toUpperCase()}
                            </MenuItem>
                        ])}
                    </Menu>
                </React.Fragment>
            );
        }

        const path = PAGE_ROUTES[page.type];
        if (!path) return null;

        const labelKey = page.type === "analytics" ? "menu.analyses"
            : page.type === "origins" ? "menu.origin"
            : page.type === "manuel" ? "menu.howto"
            : `menu.${page.type}`;

        return (
            <NavButton
                key={index}
                label={t(labelKey)}
                path={path}
                startIcon={icon}
                navigate={navigate}
                isActive={isActive(path)}
            />
        );
    }

    if (!config) return null;

    return (
        <AppBar position="static" elevation={2} style={{background: config.color}}>
            <Toolbar sx={{display: "flex", alignItems: "center", gap: 0.5, minHeight: 56, px: {xs: 1, sm: 2}}}>


                {/* ── Navigation principale (sans les pages "outils") ── */}
                {config.pages && config.pages.map((page, index) => {
                    if (TOOLS_TYPES.has(page.type)) return null;
                    return getButton(page, index);
                })}

                {/* ── Mes outils + Admin (menu fusionné) ── */}
                {config.pages && (() => {
                    const toolsPages = config.pages.filter(p => TOOLS_TYPES.has(p.type) && canDisplay(p));
                    if (toolsPages.length === 0 && !isAdmin) return null;

                    return (
                        <>
                            <VDivider/>
                            {renderMergedMenu(toolsPages)}
                        </>
                    );
                })()}


                <Box component="div" sx={{flexGrow: 1}}/>

                {/* ── Mode édition CMS (ADMIN / WRITER) ── */}
                {canEdit && (
                    <>
                        <VDivider/>
                        <Button
                            size="small"
                            onClick={() => setEditMode(prev => !prev)}
                            startIcon={editMode ? <EditOffRoundedIcon fontSize="small"/> : <EditRoundedIcon fontSize="small"/>}
                            sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: "20px",
                                px: 1.5,
                                py: 0.55,
                                minWidth: "auto",
                                color: editMode ? "#fff" : "rgba(255,255,255,0.85)",
                                bgcolor: editMode ? "rgba(255,193,7,0.35)" : "rgba(255,193,7,0.15)",
                                border: "1px solid rgba(255,193,7,0.5)",
                                "&:hover": {bgcolor: editMode ? "rgba(255,193,7,0.45)" : "rgba(255,193,7,0.25)"},
                                transition: "all 0.2s ease",
                            }}
                        >
                            {editMode ? t("cms.editOn", "Édition") : t("cms.edit", "Éditer")}
                        </Button>

                        {editMode && (
                            <>
                                {/* Menu gestion des pages visibles */}
                                <Button
                                    size="small"
                                    color="inherit"
                                    onClick={(e) => setAnchorElPages(e.currentTarget)}
                                    startIcon={<VisibilityRoundedIcon fontSize="small"/>}
                                    endIcon={<KeyboardArrowDownIcon sx={{
                                        fontSize: "18px !important",
                                        transition: "transform 0.2s",
                                        transform: openPages ? "rotate(180deg)" : "rotate(0deg)",
                                    }}/>}
                                    sx={pillSx(false)}
                                >
                                    {t("cms.pages", "Pages")}
                                </Button>
                                <Menu
                                    anchorEl={anchorElPages}
                                    open={openPages}
                                    onClose={() => setAnchorElPages(null)}
                                    elevation={4}
                                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                                    transformOrigin={{vertical: "top", horizontal: "left"}}
                                    slotProps={{
                                        paper: {sx: {minWidth: 250, mt: 0.5, borderRadius: 2, px: 0.5, py: 0.5}},
                                    }}
                                >
                                    {/* ── Pages activées (ordre modifiable, hors pages outils) ── */}
                                    <Box sx={{px: 1.5, pb: 0.5}}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700}
                                                    sx={{letterSpacing: 0.5, textTransform: "uppercase"}}>
                                            {t("cms.activePages", "Pages actives")}
                                        </Typography>
                                    </Box>
                                    {(config.pages || [])
                                        .map((page, idx) => ({page, idx}))
                                        .filter(({page}) => !TOOLS_TYPES.has(page.type))
                                        .map(({page, idx}) => {
                                        const type = page.type;
                                        const icon = PAGE_ICONS[type];
                                        const labelKey = type === "analytics" ? "menu.analyses"
                                            : type === "origins" ? "menu.origin"
                                            : type === "manuel" ? "menu.howto"
                                            : `menu.${type}`;
                                        return (
                                            <MenuItem
                                                key={type}
                                                draggable
                                                onDragStart={() => handleDragStart(idx)}
                                                onDragEnter={() => handleDragEnter(idx)}
                                                onDragEnd={handleDragEnd}
                                                onDragOver={(e) => e.preventDefault()}
                                                sx={{
                                                    borderRadius: 1.5, gap: 0.5, py: 0.25,
                                                    cursor: "grab",
                                                    "&:active": {cursor: "grabbing"},
                                                }}
                                                disableRipple
                                            >
                                                <DragIndicatorRoundedIcon sx={{fontSize: 16, color: "text.disabled", mr: 0.5}}/>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1, flex: 1}}>
                                                    {icon && React.cloneElement(icon, {sx: {color: "primary.main"}})}
                                                    <Typography variant="body2">{t(labelKey)}</Typography>
                                                </Box>
                                                <IconButton size="small"
                                                            onClick={(e) => { e.stopPropagation(); togglePageVisibility(type); }}>
                                                    <VisibilityRoundedIcon sx={{fontSize: 16}} color="primary"/>
                                                </IconButton>
                                            </MenuItem>
                                        );
                                    })}

                                    {/* ── Pages désactivées (hors pages outils) ── */}
                                    {Object.keys(PAGE_ROUTES)
                                        .filter(type => !TOOLS_TYPES.has(type))
                                        .filter(type => !config.pages?.some(p => p.type === type)).length > 0 && [
                                            <Divider key="inactive-divider" sx={{my: 0.75}}/>,
                                            <Box key="inactive-header" sx={{px: 1.5, pb: 0.5}}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={700}
                                                            sx={{letterSpacing: 0.5, textTransform: "uppercase"}}>
                                                    {t("cms.inactivePages", "Pages inactives")}
                                                </Typography>
                                            </Box>,
                                            ...Object.keys(PAGE_ROUTES)
                                                .filter(type => !TOOLS_TYPES.has(type))
                                                .filter(type => !config.pages?.some(p => p.type === type))
                                                .map(type => {
                                                    const icon = PAGE_ICONS[type];
                                                    const labelKey = type === "analytics" ? "menu.analyses"
                                                        : type === "origins" ? "menu.origin"
                                                        : type === "manuel" ? "menu.howto"
                                                        : `menu.${type}`;
                                                    return (
                                                        <MenuItem
                                                            key={type}
                                                            onClick={() => togglePageVisibility(type)}
                                                            sx={{borderRadius: 1.5, gap: 1, opacity: 0.6}}
                                                        >
                                                            <Box sx={{display: "flex", alignItems: "center", gap: 1, flex: 1}}>
                                                                {icon && React.cloneElement(icon, {sx: {color: "text.disabled"}})}
                                                                <Typography variant="body2" color="text.disabled">
                                                                    {t(labelKey)}
                                                                </Typography>
                                                            </Box>
                                                            <VisibilityOffRoundedIcon sx={{fontSize: 16, color: "text.disabled"}}/>
                                                        </MenuItem>
                                                    );
                                                }),
                                    ]}

                                    {/* ── Outils (ADMIN uniquement) ── */}
                                    {isAdmin && [
                                        <Divider key="tools-divider" sx={{my: 0.75}}/>,
                                        <Box key="tools-header" sx={{px: 1.5, pb: 0.5}}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}
                                                        sx={{letterSpacing: 0.5, textTransform: "uppercase"}}>
                                                {t("cms.tools", "Outils")}
                                            </Typography>
                                        </Box>,
                                        ...TOOLS_ORDER.map(type => {
                                            const icon = PAGE_ICONS[type];
                                            const enabled = config.pages?.some(p => p.type === type);
                                            const labelKey = type === "analytics" ? "menu.analyses"
                                                : type === "dashboards" ? "menu.dashboards"
                                                : `menu.${type}`;
                                            return (
                                                <MenuItem
                                                    key={"tool-" + type}
                                                    onClick={() => togglePageVisibility(type)}
                                                    sx={{borderRadius: 1.5, gap: 1, opacity: enabled ? 1 : 0.6}}
                                                >
                                                    <Box sx={{display: "flex", alignItems: "center", gap: 1, flex: 1}}>
                                                        {icon && React.cloneElement(icon, {
                                                            sx: {color: enabled ? "primary.main" : "text.disabled"}
                                                        })}
                                                        <Typography variant="body2" sx={{
                                                            color: enabled ? "text.primary" : "text.disabled"
                                                        }}>
                                                            {t(labelKey)}
                                                        </Typography>
                                                    </Box>
                                                    {enabled
                                                        ? <VisibilityRoundedIcon sx={{fontSize: 16}} color="primary"/>
                                                        : <VisibilityOffRoundedIcon sx={{fontSize: 16, color: "text.disabled"}}/>
                                                    }
                                                </MenuItem>
                                            );
                                        }),
                                    ]}

                                    {/* ── Rapports (si la page rapports est activée) ── */}
                                    {config.pages?.some(p => p.type === "reports") && [
                                        <Divider key="reports-edit-divider" sx={{my: 0.75}}/>,
                                        <Box key="reports-edit-header" sx={{px: 1.5, pb: 0.5}}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}
                                                        sx={{letterSpacing: 0.5, textTransform: "uppercase"}}>
                                                {t("menu.reports", "Rapports")}
                                            </Typography>
                                        </Box>,
                                        ...Object.keys(config.reports || {}).map(report => (
                                            <MenuItem
                                                key={"report-edit-" + report}
                                                disableRipple
                                                sx={{borderRadius: 1.5, gap: 1, py: 0.25}}
                                            >
                                                <SummarizeRoundedIcon fontSize="small" sx={{color: "text.secondary", flexShrink: 0}}/>
                                                <Typography variant="body2" sx={{flex: 1}}>{report.toUpperCase()}</Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); deleteReport(report); }}
                                                    sx={{"&:hover": {color: "error.main"}}}
                                                >
                                                    <DeleteRoundedIcon sx={{fontSize: 15}}/>
                                                </IconButton>
                                            </MenuItem>
                                        )),
                                        <Box key="reports-edit-add"
                                             sx={{px: 1, pt: 0.25, pb: 0.5, display: "flex", gap: 0.5, alignItems: "center"}}
                                             onKeyDown={(e) => e.stopPropagation()}
                                        >
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                placeholder={t("cms.newReport", "Nouveau rapport…")}
                                                value={newReportName}
                                                onChange={(e) => setNewReportName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    e.stopPropagation();
                                                    if (e.key === "Enter") addReport(newReportName);
                                                }}
                                                sx={{flex: 1, "& .MuiOutlinedInput-root": {borderRadius: 1.5, fontSize: "0.8rem"}}}
                                                slotProps={{input: {style: {padding: "4px 8px"}}}}
                                            />
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => addReport(newReportName)}
                                                disabled={!newReportName.trim()}
                                            >
                                                <AddRoundedIcon sx={{fontSize: 16}}/>
                                            </IconButton>
                                        </Box>,
                                    ]}
                                </Menu>

                                {/* Bouton sauvegarder (icône) */}
                                <IconButton
                                    size="small"
                                    color="inherit"
                                    onClick={handleSave}
                                    disabled={saving}
                                    sx={{
                                        bgcolor: "rgba(255,255,255,0.12)",
                                        border: "1px solid rgba(255,255,255,0.35)",
                                        "&:hover": {bgcolor: "rgba(255,255,255,0.22)"},
                                    }}
                                >
                                    <SaveRoundedIcon fontSize="small"/>
                                </IconButton>
                            </>
                        )}
                    </>
                )}

                {/* ── Contrôles utilisateur ── */}
                <VDivider/>
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    <UserToken user={user} disconnect={disconnect} pendingCount={pendingCount} isAdmin={isAdmin}/>
                    <LngToken/>

                    {/* ── Bouton affichage (palette + mode nuit) ── */}
                    <Button
                        size="small"
                        color="inherit"
                        onClick={(e) => setAnchorElDisplay(e.currentTarget)}
                        aria-controls={openDisplay ? "display-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={openDisplay ? "true" : undefined}
                        sx={{
                            ...pillSx(darkMode || paletteMode !== "default"),
                            minWidth: "auto", px: 1,
                        }}
                    >
                        {darkMode
                            ? <DarkModeRoundedIcon fontSize="small"/>
                            : paletteMode !== "default"
                                ? <ContrastRoundedIcon fontSize="small"/>
                                : <LightModeRoundedIcon fontSize="small"/>
                        }
                    </Button>

                    <Menu
                        id="display-menu"
                        anchorEl={anchorElDisplay}
                        open={openDisplay}
                        onClose={() => setAnchorElDisplay(null)}
                        elevation={4}
                        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                        transformOrigin={{vertical: "top", horizontal: "right"}}
                        slotProps={{
                            paper: {sx: {minWidth: 230, mt: 0.5, borderRadius: 2, px: 0.5, py: 0.5}},
                        }}
                    >
                        {/* ── Mode nuit ── */}
                        <MenuItem
                            onClick={toggleDarkMode}
                            sx={{borderRadius: 1.5, gap: 1, justifyContent: "space-between"}}
                        >
                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                {darkMode
                                    ? <DarkModeRoundedIcon fontSize="small" sx={{color: "primary.main"}}/>
                                    : <LightModeRoundedIcon fontSize="small" sx={{color: "text.secondary"}}/>
                                }
                                <Typography variant="body2">Mode nuit</Typography>
                            </Box>
                            <Switch
                                size="small"
                                checked={darkMode}
                                onChange={toggleDarkMode}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </MenuItem>

                        <Divider sx={{my: 0.75}}/>

                        {/* ── Palette ── */}
                        <Box sx={{px: 1.5, pb: 0.5}}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{letterSpacing: 0.5, textTransform: "uppercase"}}>
                                Palette
                            </Typography>
                        </Box>

                        {Object.values(PALETTES).map((palette) => {
                            const isSelected = paletteMode === palette.id;
                            return (
                                <MenuItem
                                    key={palette.id}
                                    onClick={() => { setPaletteMode(palette.id); setAnchorElDisplay(null); }}
                                    sx={{borderRadius: 1.5, gap: 1}}
                                >
                                    {/* Swatches */}
                                    <Box sx={{display: "flex", gap: 0.4, flexShrink: 0}}>
                                        {palette.swatches.map((color) => (
                                            <Box key={color} sx={{
                                                width: 11, height: 11, borderRadius: "50%",
                                                backgroundColor: color,
                                                border: "1px solid rgba(0,0,0,0.15)",
                                            }}/>
                                        ))}
                                    </Box>
                                    <Typography variant="body2" sx={{flex: 1, fontWeight: isSelected ? 700 : 400}}>
                                        {palette.label}
                                    </Typography>
                                    {isSelected && <CheckRoundedIcon fontSize="small" color="primary"/>}
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </Box>

            </Toolbar>
        </AppBar>
    );
}
